/**
 * The write side of 我的灯塔客户 once a dialog has closed: runs the adds, and reports every
 * outcome — adds and removals alike — as notices above the block (DAT-9086).
 *
 * ⭐ The dialogs do not hold results. `Add Lighthouse Account` closes on Confirm and hands its
 * picks here; what happened to each is said in one place on the page (user, 2026-09-30), in the
 * "X added, Y failed" shape `MotionWaveSelectPanel` uses.
 *
 * ⚠️ Rendered as Cloudscape `Alert`s, NOT a `Flashbar`, though the notices are Flashbar-shaped.
 * Dismissing a Flashbar's last message moves focus to the page's first <h1> (Cloudscape gives
 * `Box variant="h1"` a tabIndex of -1 for exactly that), which here is the greeting at the top —
 * so closing a notice mid-page scrolled the reader back to the top. Flashbar is the page-level
 * notification slot; an Alert is the in-content one, and it leaves focus alone.
 *
 * Adds go out at most ADD_CONCURRENCY at a time. Each `PutAccountFlag` borrows four to five
 * database connections on the service (memory `reference_ivt_per_request_db_connection_cost`),
 * so a ten-account pick fired at once would hold forty-odd.
 *
 * A failure is reported, not retried: the notice names each account and why, and that is all
 * (user, 2026-09-30 — no retry button). A `denied` one's message says what to ask for.
 */

import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { AccountFlag } from '@amzn/ivthub-client';
import type { AccountFlagFailure, AccountFlagState } from './useAccountFlag';

export const ADD_CONCURRENCY = 3;

/** One pick from the dialog: the id to write, and the label to report it under. */
export interface LighthouseTarget {
  accountId: string;
  label: string;
}

/** One notice, in the members an Alert takes; `loading` adds a spinner before the content. */
export interface LighthouseNotice {
  id: string;
  type: 'success' | 'error' | 'info';
  header?: string;
  content: ReactNode;
  loading?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
}

export interface LighthouseEditsState {
  notices: LighthouseNotice[];
  /**
   * True while a batch of adds is running. The caller disables Add and Delete for that long
   * (user, 2026-09-30): the dialog closes on Confirm, so without this a second batch — or a delete —
   * could start on top of the first, and the two would overwrite each other's notices, run past
   * ADD_CONCURRENCY together, and (for an account in both) let useAccountFlag's in-flight guard
   * report the second attempt as added whatever the first one's outcome.
   */
  busy: boolean;
  add: (targets: LighthouseTarget[]) => Promise<void>;
  /** Report a removal the delete dialog completed. */
  announceRemoved: (label: string) => void;
}

/** `fn` over `items`, never more than `limit` in flight, results in input order. */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return out;
}

const PROGRESS_ID = 'lighthouse-progress';
const ADDED_ID = 'lighthouse-added';
const FAILED_ID = 'lighthouse-failed';
const REMOVED_ID = 'lighthouse-removed';

/**
 * @param setFlag `useAccountFlag().setFlag` — it resolves to the failure rather than throwing.
 * @param onChanged refetch whatever the flag moves: an added account leaves 圈外归仓 for 我的灯塔客户.
 */
export function useLighthouseEdits(
  setFlag: AccountFlagState['setFlag'],
  onChanged: () => void,
): LighthouseEditsState {
  const [notices, setNotices] = useState<LighthouseNotice[]>([]);
  const [busy, setBusy] = useState(false);
  // The guard itself: `busy` is rendered state, so two calls in one tick would both read false.
  const running = useRef(false);

  const dismiss = useCallback(
    (id: string) => () => setNotices((items) => items.filter((f) => f.id !== id)),
    [],
  );

  const add = useCallback(
    async (targets: LighthouseTarget[]) => {
      if (targets.length === 0 || running.current) return;
      running.current = true;
      setBusy(true);
      try {
        // A new batch replaces whatever the last one said: two batches' messages side by side
        // cannot be told apart.
        setNotices([
          {
            id: PROGRESS_ID,
            type: 'info',
            loading: true,
            content: `正在添加 ${targets.length} 个灯塔客户…`,
          },
        ]);

        const results = await mapWithConcurrency(targets, ADD_CONCURRENCY, (t) =>
          setFlag(t.accountId, AccountFlag.LIGHTHOUSE),
        );
        const added = targets.filter((_, i) => results[i] === null);
        const failed = targets
          .map((target, i) => ({ target, failure: results[i] }))
          .filter((r): r is { target: LighthouseTarget; failure: AccountFlagFailure } => r.failure !== null);

        // Refetch on ANY success, partial included: the added ones have moved blocks either way.
        if (added.length > 0) onChanged();

        const items: LighthouseNotice[] = [];
        if (added.length > 0) {
          items.push({
            id: ADDED_ID,
            type: 'success',
            dismissible: true,
            onDismiss: dismiss(ADDED_ID),
            // The names are the point: a new account with nothing in the window reads $0 and sorts
            // last, so it is usually NOT on the page being looked at.
            content: `已添加 ${added.length} 个灯塔客户：${added.map((t) => t.label).join('、')}`,
          });
        }
        if (failed.length > 0) {
          items.push({
            id: FAILED_ID,
            type: 'error',
            dismissible: true,
            onDismiss: dismiss(FAILED_ID),
            header: `${failed.length} 个客户未能添加`,
            content: (
              <ul className="ivt-dashboard__notice-list">
                {failed.map(({ target, failure }) => (
                  <li key={target.accountId}>
                    {target.label}：{failure.message}
                  </li>
                ))}
              </ul>
            ),
          });
        }
        setNotices(items);
      } finally {
        running.current = false;
        setBusy(false);
      }
    },
    [setFlag, onChanged, dismiss],
  );

  const announceRemoved = useCallback(
    (label: string) =>
      setNotices([
        {
          id: REMOVED_ID,
          type: 'success',
          dismissible: true,
          onDismiss: dismiss(REMOVED_ID),
          content: `已将「${label}」移出灯塔客户`,
        },
      ]),
    [dismiss],
  );

  return { notices, busy, add, announceRemoved };
}
